/**
 * 견적서 품목/상세내용 자동완성 — 주요 가격표 (main_product_price) 기반.
 *
 * Props:
 *   field='name' or 'description' — 검색 대상 컬럼
 *   value         현재 입력 값
 *   onChange      직접 입력 변경 (자유 텍스트)
 *   onPick        가격표 항목 선택 시 (전체 row 반환 — 호출자가 단가도 채울 수 있게)
 *
 * 가격표 데이터는 부모에서 1회 로드해 prop 으로 내려줌 (price-list-api 의 listMainProducts).
 * field='name' → name 검색·표시
 * field='description' → description 검색·표시 (긴 텍스트면 줄임)
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { MainProductRow } from '../lib/price-list-api';

interface Props {
  field: 'name' | 'description';
  value: string;
  products: MainProductRow[];
  onChange: (v: string) => void;
  onPick: (row: MainProductRow) => void;
  placeholder?: string;
  className?: string;
  // description 모드 — Excel 열 너비와 동일하게 줄바꿈되도록 textarea max-width 적용
  inputMaxWidthPx?: number;
}

export function QuoteItemAutocomplete({
  field,
  value,
  products,
  onChange,
  onPick,
  placeholder,
  className,
  inputMaxWidthPx,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const ref = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  // description 모드일 때 textarea 높이를 내용에 맞춰 자동 조절
  useEffect(() => {
    if (field !== 'description') return;
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [query, field]);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const matches = useMemo(() => {
    const q = (query ?? '').trim().toLowerCase();
    if (!q) return products.slice(0, 8);
    // 검색 매칭은 name + description + note 모두 — 외부 노출명·코드명 어느 쪽으로도 검색 가능
    return products
      .filter((p) => {
        const haystack = [p.name, p.description, p.note]
          .filter((s) => s != null)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 8);
  }, [query, products]);

  return (
    <div ref={ref} className={`relative ${className ?? ''}`}>
      {field === 'description' ? (
        <textarea
          ref={textareaRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            if (!open) setOpen(true);
            // 내용 변할 때마다 즉시 높이 재계산 (useEffect 와 중복되지만 IME 입력 등 미스 방지)
            e.target.style.height = 'auto';
            e.target.style.height = `${e.target.scrollHeight}px`;
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder ?? '상세 내용'}
          rows={1}
          style={inputMaxWidthPx ? { maxWidth: `${inputMaxWidthPx}px` } : undefined}
          className="ui-input ui-input-sm ui-textarea w-full min-h-[1.75rem] py-1 leading-snug resize-none overflow-hidden"
        />
      ) : (
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder ?? '품목명'}
          className="ui-input ui-input-sm w-full"
        />
      )}
      {open && matches.length > 0 && (
        <div className="absolute z-30 left-0 right-0 top-full mt-0.5 max-h-72 overflow-y-auto bg-white border border-slate-300 rounded shadow-lg">
          <div className="px-2 py-1 text-[11px] text-slate-500 bg-slate-50 border-b border-slate-200 sticky top-0">
            주요 가격표에서 선택 ({matches.length}건) — 또는 직접 입력
          </div>
          {matches.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                onPick(p);
                setOpen(false);
              }}
              className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
            >
              <div className="font-medium text-slate-800">{p.name}</div>
              <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 whitespace-pre-wrap">
                {p.description}
              </div>
              <div className="text-[11px] text-emerald-700 mt-0.5">
                공급단가 {Number(p.supplyPrice).toLocaleString()}원
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
